import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { FIREBASE_DB } from '../firebase.config';
import { Mail, Clock, CheckCircle, XCircle, FileText, Download, Filter, Eye, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const ITEMS_PER_PAGE = 10; // Number of items to show per page

const AdminLogsPage = () => {
  const [w2Logs, setW2Logs] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('w2');
  const [limitCount, setLimitCount] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Calculate paginated logs
  const getPaginatedLogs = (logs) => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return logs.slice(startIndex, endIndex);
  };

  // Update total pages when logs or limit changes
  useEffect(() => {
    const totalLogs = activeTab === 'w2' ? filteredW2Logs.length : filteredEmailLogs.length;
    setTotalPages(Math.ceil(totalLogs / limitCount));
    // Reset to first page when switching tabs or changing limit
    setCurrentPage(1);
  }, [activeTab, limitCount, w2Logs.length, emailLogs.length, searchTerm]);

  useEffect(() => {
    fetchLogs();
  }, [limitCount]);

  // Filter logs based on search term
  const filteredW2Logs = w2Logs.filter(log => {
    const searchStr = searchTerm.toLowerCase();
    return (
      log.employeeName?.toLowerCase().includes(searchStr) ||
      log.email?.toLowerCase().includes(searchStr) ||
      log.userId?.toLowerCase().includes(searchStr) ||
      log.ipAddress?.toLowerCase().includes(searchStr) ||
      log.year?.toString().includes(searchStr)
    );
  });

  const filteredEmailLogs = emailLogs.filter(log => {
    const searchStr = searchTerm.toLowerCase();
    return (
      log.recipientName?.toLowerCase().includes(searchStr) ||
      log.recipientEmail?.toLowerCase().includes(searchStr) ||
      log.type?.toLowerCase().includes(searchStr) ||
      log.status?.toLowerCase().includes(searchStr)
    );
  });

  const fetchLogs = async () => {
    try {
      setLoading(true);
      
      // Fetch W2 access logs
      const w2Query = query(
        collection(FIREBASE_DB, 'w2AccessLogs'),
        orderBy('accessedAt', 'desc'),
        limit(1000) // Fetch more logs for client-side pagination
      );
      
      // Fetch email logs
      const emailQuery = query(
        collection(FIREBASE_DB, 'emailLogs'),
        orderBy('sentAt', 'desc'),
        limit(1000) // Fetch more logs for client-side pagination
      );
      
      const [w2Snapshot, emailSnapshot] = await Promise.all([
        getDocs(w2Query),
        getDocs(emailQuery)
      ]);

      // Process W2 logs and fetch employee names
      const w2LogsPromises = w2Snapshot.docs.map(async docSnapshot => {
        const data = docSnapshot.data();
        let employeeName = data.employeeName;

        // Try to get name from users collection
        if (!employeeName) {
          try {
            // First try by userId if available
            if (data.userId) {
              const userDocRef = doc(FIREBASE_DB, 'users', data.userId);
              const userDoc = await getDoc(userDocRef);
              if (userDoc.exists()) {
                const userData = userDoc.data();
                employeeName = userData.name || userData.employeeData?.name;
              }
            }
            
            // If still no name and we have an email, try finding by email
            if (!employeeName && data.email) {
              const usersQuery = query(
                collection(FIREBASE_DB, 'users'),
                where('email', '==', data.email)
              );
              const userSnapshot = await getDocs(usersQuery);
              if (!userSnapshot.empty) {
                const userData = userSnapshot.docs[0].data();
                employeeName = userData.name || userData.employeeData?.name;
              }
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        }

        return {
          id: docSnapshot.id,
          ...data,
          employeeName: employeeName || 'Unknown',
          accessedAt: data.accessedAt?.toDate?.()
        };
      });

      const processedW2Logs = await Promise.all(w2LogsPromises);
      setW2Logs(processedW2Logs);
      
      setEmailLogs(emailSnapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
        sentAt: docSnapshot.data().sentAt?.toDate?.()
      })));
      
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const renderPagination = (totalItems) => {
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const pages = [];
    
    // Always show first page, last page, current page, and one page before and after current
    const pageNumbers = new Set();
    pageNumbers.add(1);
    pageNumbers.add(totalPages);
    pageNumbers.add(currentPage);
    pageNumbers.add(currentPage - 1);
    pageNumbers.add(currentPage + 1);

    const sortedPages = Array.from(pageNumbers)
      .filter(p => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);

    // Add pagination items with ellipsis
    sortedPages.forEach((pageNum, index) => {
      if (index > 0 && pageNum - sortedPages[index - 1] > 1) {
        pages.push(
          <span key={`ellipsis-${pageNum}`} className="px-3 py-2 text-gray-500">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={pageNum}
          onClick={() => handlePageChange(pageNum)}
          className={`px-3 py-2 rounded-md ${
            currentPage === pageNum
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          {pageNum}
        </button>
      );
    });

    return (
      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
              currentPage === 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
              currentPage === totalPages
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">
                {totalItems === 0 ? 0 : Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalItems)}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)}
              </span>{' '}
              of <span className="font-medium">{totalItems}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                  currentPage === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {pages}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                  currentPage === totalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const renderW2Logs = (logs) => (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Year</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {getPaginatedLogs(logs).map((log) => {
              // Default to 'view' if accessType is undefined
              const accessType = log.accessType || 'view';
              return (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {log.employeeName || log.email || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {log.email && log.employeeName ? log.email : log.userId || 'No ID'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${accessType === 'view' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                      {accessType === 'view' ? (
                        <Eye className="h-4 w-4 mr-1" />
                      ) : (
                        <Download className="h-4 w-4 mr-1" />
                      )}
                      {accessType.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{log.year || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {log.accessedAt ? new Date(log.accessedAt).toLocaleString() : 'Date not available'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{log.ipAddress || 'Unknown IP'}</div>
                    <div className="text-xs text-gray-400">{log.userAgent || 'Unknown Device'}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {logs.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            {searchTerm ? 'No matching W2 logs found' : 'No W2 access logs found'}
          </div>
        )}
      </div>
      {logs.length > 0 && renderPagination(logs.length)}
    </>
  );

  const renderEmailLogs = (logs) => (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {getPaginatedLogs(logs).map((log) => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{log.recipientName}</div>
                  <div className="text-sm text-gray-500">{log.recipientEmail}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{log.type}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${log.status === 'SUCCESS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {log.status === 'SUCCESS' ? (
                      <CheckCircle className="h-4 w-4 mr-1" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-1" />
                    )}
                    {log.status}
                  </span>
                  {log.error && (
                    <div className="text-xs text-red-500 mt-1">{log.error}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {log.sentAt ? new Date(log.sentAt).toLocaleString() : 'Date not available'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            {searchTerm ? 'No matching email logs found' : 'No email logs found'}
          </div>
        )}
      </div>
      {logs.length > 0 && renderPagination(logs.length)}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-semibold text-gray-900">System Logs</h1>
              <div className="flex items-center space-x-4">
                {/* Search Bar */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-64 sm:text-sm"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Filter className="h-5 w-5 text-gray-400" />
                  <select
                    value={limitCount}
                    onChange={(e) => setLimitCount(Number(e.target.value))}
                    className="border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={50}>Last 50</option>
                    <option value={100}>Last 100</option>
                    <option value={200}>Last 200</option>
                  </select>
                </div>
                <button
                  onClick={fetchLogs}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Refresh
                </button>
              </div>
            </div>
            <div className="mt-4">
              <nav className="flex space-x-4">
                <button
                  onClick={() => setActiveTab('w2')}
                  className={`px-3 py-2 font-medium text-sm rounded-md ${
                    activeTab === 'w2'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FileText className="h-5 w-5 inline-block mr-1" />
                  W2 Documents
                </button>
                <button
                  onClick={() => setActiveTab('email')}
                  className={`px-3 py-2 font-medium text-sm rounded-md ${
                    activeTab === 'email'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Mail className="h-5 w-5 inline-block mr-1" />
                  Email Notifications
                </button>
              </nav>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {activeTab === 'w2' ? (
                  <>
                    {renderW2Logs(filteredW2Logs)}
                  </>
                ) : (
                  <>
                    {renderEmailLogs(filteredEmailLogs)}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogsPage; 
